import { type ReactNode, useCallback } from "react";

import { Icon_Globe24, Icon_Lock24, Icon_TeamFilled24 } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import useAccount from "../../../../../hooks/useAccount";
import {
  useMutateProjectViewLink,
  useProjectViewLinkSharing,
} from "../../../../../hooks/useProjectViewLink";
import { useTracker } from "../../../../../hooks/useTracker";
import {
  useWebExport,
  type WebExportAssignableSharedState,
  WebExportAssignableSharedStateDisplayName,
  WebExportAssignableSharedStates,
  WebExportAssignableUserRoles,
  WebExportAssignableUserRolesDisplayName,
} from "../../../../../hooks/useWebExport";
import { GeneralAccess } from "./GeneralAccess";

export const GeneralAccessViewLink = () => {
  const tracker = useTracker();
  const { data: webExportData } = useWebExport();
  const { data: accountData } = useAccount();
  const { viewLinkId = "" } = useParams();

  const { data: projectViewLinkSharingData } = useProjectViewLinkSharing();
  const { mutateAsync: mutateProjectViewLink, isPending } = useMutateProjectViewLink();

  const getAccessIcon = (access: WebExportAssignableSharedState): ReactNode => {
    switch (access) {
      case "anyone_with_link":
        return <Icon_Globe24 />;
      case "team_only":
        return <Icon_TeamFilled24 />;
      case "restricted":
        return <Icon_Lock24 />;
    }
  };

  const getTeamName = useCallback(() => {
    return accountData?.teams?.find((team) => team.id === webExportData?.teamId)?.name;
  }, [accountData?.teams, webExportData?.teamId]);

  return (
    <GeneralAccess
      accessOptions={WebExportAssignableSharedStates}
      roles={WebExportAssignableUserRoles}
      initialGeneralAccess={
        projectViewLinkSharingData?.sharedState as WebExportAssignableSharedState
      }
      initialBaseRole={projectViewLinkSharingData?.baseRole}
      isDisabled={isPending}
      accessDisplayName={(state) => WebExportAssignableSharedStateDisplayName(state, getTeamName())}
      roleDisplayName={WebExportAssignableUserRolesDisplayName}
      getAccessIcon={getAccessIcon}
      restrictedAccessValue="restricted"
      onSubmit={async (generalAccess, baseRole) => {
        await mutateProjectViewLink({
          viewLinkId,
          body: {
            sharedState: generalAccess,
            baseRole: baseRole,
          },
        });
      }}
      onGeneralAccessChange={(newValue, oldValue) => {
        tracker.trackShareModalGeneralPermissionChanged({
          newValue,
          viewLinkId,
          webExportId: webExportData?.webExport.webExportId,
          oldValue,
        });
      }}
      onBaseRoleChange={(newValue, oldValue) => {
        tracker.trackShareModalGeneralRoleChanged({
          newValue,
          viewLinkId,
          webExportId: webExportData?.webExport.webExportId,
          oldValue,
        });
      }}
    />
  );
};
